import TIM from 'tim-js-sdk';
import { env } from '../../../util/browser-env';
import protobuf, { INamespace } from 'protobufjs/light';
import protoJson from '../../proto/proto.json';
import * as im from '../../../typings/model/im';
import {
  BanSignalingMsg,
  CentreConfigChange,
  ChannelAtMsgSeqsReq,
  ChannelAtMsgSeqsRsp,
  ChanSignalingMsg,
  ChgRoleMsg,
  ChgRoleUserMemMsg,
  GroupChangeSignalingMsg,
  GroupSignalingMsg,
  H5UserRelationChangeSignalingMsg,
  MsgL2Type,
  MsgScope,
  MsgSignalingType,
  MsgType,
  ReactionMsg,
  RecallGroupMsgRsp,
  SignalingMsg,
  SystemMsg,
  TopicSignalingMsg,
} from '../../../typings/proto';
import { Base64 } from 'js-base64';
import { UserManager } from '../user';
import { logger } from '../../../util/logger';
import Net, { RspError } from '../../../util/net';
import { report } from '../../api/report';
import ImApi from '../../api/im';
import { TSimpleUserMap } from '../../../typings/model/user';
import { getCommonOptions } from '../../../util/common-options';
import { TGetMessageTraceInfo } from '../../../typings/model/im';
import { SimpleUserManager } from '../simple-user';
const pbRoot = protobuf.Root.fromJSON(protoJson as INamespace);

// 业务侧可监听的事件
export enum MessageEventType {
  sdkReady, // SDK初始化成功
  messageReceived, // 收到新消息
  signalingReceived, // 收到新的信令消息
  messageModified,
  messageRevoked, // 消息被撤回// 信息状态更新
  messageRead, // 消息已读
  messageSendStatusChange, // 发送消息的状态变化， 发送中 - > 发送成功（失败）
  kickOut, // 用户被踢下线时触发
  sdkError, // SDK 遇到错误时触发
  netStateChange, // 网络状态发生改变
  sdkReload// 长时间断网后重新接入网络或者小程序长时间切后台又切回前台，SDK 为了和 IM 服务器进行状态及消息同步，需要重载
}

/**
 * 网络状态
 */
export const NetStatus = {
  disconnect: TIM.TYPES.NET_STATE_DISCONNECTED,
  connecting: TIM.TYPES.NET_STATE_CONNECTING,
  connected: TIM.TYPES.NET_STATE_CONNECTED,
};

// 非匿名回调，便于后期取消事件订阅
function eventHandle(event: TIM.IBaseEvent) {
  MessageManager.getInstance().handleEvent(event);
}
const tag = 'MessageManager';

type TGetMessageListParam = {
  nextReqMessageID: string;
  count?: number;
  isC2C?: boolean;
  needAddUserInfo?: boolean;
};

export interface ISendMessageOptions {
  preHandle?: im.TPreHandle | null;
  isC2C?: boolean;
}

export class MessageManager {
  private static instance: MessageManager;

  public static getInstance(): MessageManager {
    if (!MessageManager.instance) {
      MessageManager.instance = new MessageManager();
    }

    return MessageManager.instance;
  }

  /**
   * 过滤并格式化消息列表
   * @param msgList 消息列表
   * @param filterChannelIdList 过滤保留消息的频道id列表
   * @param needAddUserInfo 是否需要添加用户信息(默认：需要)
   */
  static async filterAndFormatMsgList(
    msgList: im.IMsg[],
    filterChannelIdList: string[] = [],
    needAddUserInfo = true,
  ): Promise<im.IMsg[]> {
    let filterMsgList = msgList;
    if (filterChannelIdList.length > 0) {
      filterMsgList = msgList.filter(msg => filterChannelIdList.includes(msg?.channelMsg?.channelId));
    }

    return needAddUserInfo ? await MessageManager.addUserInfo(filterMsgList) : filterMsgList;
  }

  /**
   * 解码消息自定义内容
   * @param timMsg
   */
  static formatMsg(timMsg: im.TTimMsg): im.IMsg | null {
    if (timMsg?.payload?.data || timMsg.message) {
      const msg = timMsg.message ? timMsg.message : MessageManager.decodePbMsg(timMsg.payload.data);
      if (msg) {
        if (msg.l2Type === MsgL2Type.MsgL2TypeSystem && msg.systemMsg) {
          msg.systemMsg.systemData = MessageManager.decodePbSystemMsg(msg.systemMsg);
        } else if (msg.l2Type === MsgL2Type.MsgL2TypeSignaling && msg.signalingMsg) {
          msg.signalingMsg.signalingData = MessageManager.decodePbSignalingMsg(msg.signalingMsg);
        }
        msg.reportId = msg?.channelMsg ? `${msg.channelMsg?.channelId}_${timMsg.sequence}` : `${msg.c2cMsg?.receiverUid}_${timMsg.sequence}`;
        msg.clientId = '';
        msg.origin = timMsg;
        msg.sendStatus = im.SendStatusType.success;
        return msg;
      }
    }
    return null;
  }

  static messageListSort(msgList: Array<im.IMsg>): Array<im.IMsg> {
    if (msgList && msgList.length > 1) {
      msgList.sort((a, b) => {
        const aTs = a.origin?.time || 0;
        const bTs = b.origin?.time || 0;
        if (aTs === bTs) {
          return a.origin && b.origin ? a.origin.sequence - b.origin.sequence : 0;
        }
        return aTs - bTs;
      });
    }
    return msgList || [];
  }

  /**
   * 获取sdk appid
   * @private
   */
  private static getSdkAppId(): number {
    if (env.isTest) {
      return 1400487885;
    }
    return 1400497472;
  }

  /**
   * Msg pb 解码
   * @param content
   * @private
   */
  private static decodePbMsg(content: string): im.IMsg | null {
    try {
      return this.decodeProtobufData('Msg', MessageManager.base64ToArrayBuffer(content)) as im.IMsg;
    } catch (e) {
      logger.error('decodePbMsg', e);
      return null;
    }
  }

  /**
   * 系统消息内容pb解码
   * @param systemMsg
   * @private
   */
  private static decodePbSystemMsg(systemMsg: SystemMsg) {
    try {
      // / todo 系统消息类型的语义化
      switch (systemMsg.systemType) {
        case 0:
        case 1:
        case 2:
        case 3:
        case 5:
        case 6:
        case 7:
        case 8:
        case 9:
        case 10:
        case 11:
        case 12:
          return this.decodeProtobufData('CommonSystemMsg', systemMsg.systemData);
        case 4:
          return this.decodeProtobufData('CommonSystemPicMsg', systemMsg.systemData);
        default:
      }
      return null;
    } catch (e) {
      logger.error('decodePbMsg', e);
      return null;
    }
  }

  /**
   * 解析pb数据
   * @param pbName
   * @param data
   */
  private static decodeProtobufData = (pbName: string, data: unknown): unknown => {
    const msgGen = pbRoot.lookupType(pbName);
    const msg = msgGen.decode(data as Uint8Array);
    return msgGen.toObject(msg, {
      longs: String,
    });
  };

  /**
   * 信令消息内容解码
   * @param signalMsg
   * @private
   */
  private static decodePbSignalingMsg(signalMsg: SignalingMsg) {
    try {
      let result;
      switch (signalMsg.signalingType) {
        case MsgSignalingType.MsgSignalingTypeUserBan:
        case MsgSignalingType.MsgSignalingTypeUserUnban:
        case MsgSignalingType.MsgSignalingTypeChannelBan:
        case MsgSignalingType.MsgSignalingTypeChannelUnban:
          result = this.decodeProtobufData('BanSignalingMsg', signalMsg.signalingData) as BanSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeH5UserRelationChange: // H5用户映射关系变更信令消息
          result = this.decodeProtobufData('H5UserRelationChangeSignalingMsg', signalMsg.signalingData) as H5UserRelationChangeSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeGroupInfoChange:
          result = this.decodeProtobufData('GroupChangeSignalingMsg', signalMsg.signalingData) as GroupChangeSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeChannelInfoChange:
        case MsgSignalingType.MsgSignalingTypeGroupJoinMember:
        case MsgSignalingType.MsgSignalingTypeGroupExitMember:
        case MsgSignalingType.MsgSignalingTypeGroupExitMemberOnlyAdmin:
          result = this.decodeProtobufData('GroupSignalingMsg', signalMsg.signalingData) as GroupSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeReactionMsg:
          result = this.decodeProtobufData('ReactionMsg', signalMsg.signalingData) as ReactionMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeChanJoinMember:
        case MsgSignalingType.MsgSignalingTypeChanExitMember:
          result = this.decodeProtobufData('ChanSignalingMsg', signalMsg.signalingData) as ChanSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeChgRoleMsg:
          // 群组角色信息变更
          result = this.decodeProtobufData('ChgRoleMsg', signalMsg.signalingData) as ChgRoleMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeChgRoleUserMemMsg:
          // 用户角色信息变更
          result = this.decodeProtobufData('ChgRoleUserMemMsg', signalMsg.signalingData) as ChgRoleUserMemMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeTopic:
          // 话题信令消息
          result = this.decodeProtobufData('TopicSignalingMsg', signalMsg.signalingData) as TopicSignalingMsg;
          break;
        case MsgSignalingType.MsgSignalingTypeCentreConfigChange:
          // 中心化信令消息
          result = this.decodeProtobufData('CentreConfigChange', signalMsg.signalingData) as CentreConfigChange;
          break;
        default:
          break;
      }
      return result;
    } catch (e) {
      logger.warn('decodePbSignalingMsg', e);
      return null;
    }
  }

  /**
   * base64转ArrayBuffer
   * @param base64
   * @private
   */
  private static base64ToArrayBuffer(base64: string): Uint8Array {
    const binaryString = window.atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * 添加消息发送者信息， 如果获取失败，则senderUserInfo为空
   * @param list
   * @private
   */
  private static addUserInfo(list: Array<im.IMsg>): Promise<Array<im.IMsg>> {
    return new Promise((resolve) => {
      const groupMap: { [key: string]: Array<string> } = {};

      list.forEach((item) => {
        // 注意，系统消息是没有senderUid
        const gid = item.channelMsg?.groupId;
        const uid = item.senderUid ? String(item.senderUid) : '';
        if (gid) {
          if (!groupMap[gid]) {
            groupMap[gid] = [];
          }
          if (uid && groupMap[gid].indexOf(uid) === -1) {
            groupMap[gid].push(uid);
          }
        } else {
          logger.warn('message has no gid', item);
        }
      });

      const requestList: Promise<TSimpleUserMap>[] = [];
      const simpleUserManager = SimpleUserManager.getInstance();
      Object.keys(groupMap).forEach((gid) => {
        requestList.push(simpleUserManager.fetchSimpleUserMap(groupMap[gid], gid));
      });

      Promise.all(requestList).then((resultList) => {
        const groupUserInfoMap: Record<string, TSimpleUserMap> = {};
        Object.keys(groupMap).forEach((gid, index) => {
          groupUserInfoMap[gid] = resultList[index] as TSimpleUserMap;
        });

        list.forEach((item) => {
          const uid = item.senderUid;
          const gid = item.channelMsg.groupId;
          if (groupUserInfoMap[gid]) {
            // eslint-disable-next-line no-param-reassign
            item.senderUserInfo = groupUserInfoMap[gid][String(uid)];
          }
        });
        resolve(list);
      })
        .catch((error) => {
          logger.error('addUserInfo', error);
          resolve(list);
        });
    });
  }

  // 是否已经初始化
  isInit = false;

  tim!: TIM.ITencentIM;

  // / 是否登录
  isLogin = false;

  isLogout = false;

  // / sdk是否ready, 要ready后才能调用相关接口
  isReady = false;

  readyPromise: Promise<unknown>;

  private readyResolve!: (value: unknown) => void;

  private listenerCallback: im.TEventCallbackList = {};

  private constructor() {
    this.readyPromise = new Promise((resolve) => {
      this.readyResolve = resolve;
    });
  }

  /**
   * 初始化
   */
  init(): void {
    if (this.isInit) return;
    this.tim = TIM.create({
      SDKAppID: MessageManager.getSdkAppId(),
    });
    const { tim } = this;
    tim.setLogLevel(2); // 普通级别，日志量较多，接入时建议使用

    tim.on(TIM.EVENT.SDK_READY, eventHandle);
    tim.on(TIM.EVENT.MESSAGE_RECEIVED, eventHandle);
    // 收到消息被第三方回调修改的通知，消息发送方可通过遍历 event.data 获取消息列表数据并更新页面上同 ID 消息的内容（v2.12.1起支持）
    tim.on(TIM.EVENT.MESSAGE_MODIFIED, eventHandle);
    // 收到消息被撤回的通知。
    tim.on(TIM.EVENT.MESSAGE_REVOKED, eventHandle);
    // 收到会话列表更新通知，可通过遍历 event.data 获取会话列表数据并渲染到页面
    // tim.on(TIM.EVENT.CONVERSATION_LIST_UPDATED, eventHandle);
    // 收到群组列表更新通知，可通过遍历 event.data 获取群组列表数据并渲染到页面
    // tim.on(TIM.EVENT.GROUP_LIST_UPDATED, eventHandle);
    // 收到 SDK 发生错误通知，可以获取错误码和错误信息
    tim.on(TIM.EVENT.ERROR, eventHandle);
    // 收到 SDK 进入 not ready 状态通知，此时 SDK 无法正常工作
    tim.on(TIM.EVENT.SDK_NOT_READY, eventHandle);
    // 收到被踢下线通知
    tim.on(TIM.EVENT.KICKED_OUT, eventHandle);
    // 网络状态发生改变（v2.5.0 起支持）
    // event.data.state 当前网络状态，枚举值及说明如下：
    //   - TIM.TYPES.NET_STATE_CONNECTED - 已接入网络
    //   - TIM.TYPES.NET_STATE_CONNECTING - 连接中。很可能遇到网络抖动，SDK 在重试。接入侧可根据此状态提示“当前网络不稳定”或“连接中”
    //   - TIM.TYPES.NET_STATE_DISCONNECTED - 未接入网络。接入侧可根据此状态提示“当前网络不可用”。SDK 仍会继续重试，若用户网络恢复，SDK 会自动同步消息
    tim.on(TIM.EVENT.NET_STATE_CHANGE, eventHandle);
    tim.on(TIM.EVENT.SDK_RELOAD, eventHandle);
    this.isInit = true;
  }

  /**
   * 使用 用户ID(userID) 和 签名串(userSig) 登录即时通信 IM，登录流程有若干个异步执行的步骤，使用返回的 Promise 对象处理登录成功或失败。
   * @param userID: string
   * @param userSig: string
   */
  login({
    userID,
    userSig,
  }: {
    userID?: string,
    userSig: string
  }): Promise<boolean> {
    const userId = userID ?? String(UserManager.getInstance().getCurrentUid());
    return new Promise((resolve, reject) => {
      this.tim.login({
        userID: userId,
        userSig,
      }).then((result: unknown) => {
        this.isLogin = true;
        logger.info(`${tag} im login`, result);
        resolve(true);
      })
        .catch((e: TIM.TError) => {
          this.isLogin = false;
          logger.imError(`${tag} im login`, e);
          reject({ msg: e?.message || 'IM登录失败，请稍后重试', code: e?.code || - 1 });
        });
    });
  }

  /**
   * 登出
   */
  logout(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isLogout) {
        return resolve(false);
      }

      this.isLogout = true;
      this.tim?.logout().then((result: unknown) => {
        logger.info(`${tag} im logout`, result);
        resolve(true);
      })
        .catch((e: TIM.TError) => {
          this.isLogout = false;
          logger.imError(`${tag} im logout`, e);
          reject({ msg: e?.message || 'IM登出失败，请稍后重试', code: e?.code || - 1 });
        });
    });
  }

  /**
   * 处理im原生事件
   * @param event
   */
  handleEvent(event: im.IBaseEvent): void {
    switch (event.name) {
      case TIM.EVENT.SDK_READY:
        this.handleSdkReady(event);
        break;
      case TIM.EVENT.MESSAGE_RECEIVED:
        this.handleMessageReceived(event as im.MessageReceivedEvent, MessageEventType.messageReceived);
        break;
      case TIM.EVENT.MESSAGE_MODIFIED:
        this.handleMessageReceived(event as im.MessageReceivedEvent, MessageEventType.messageModified);
        break;
      case TIM.EVENT.KICKED_OUT:
        this.handleKickedOut(event as im.KickedOutEvent);
        break;
      case TIM.EVENT.ERROR:
        this.handleSdkError(event);
        break;
      case TIM.EVENT.NET_STATE_CHANGE:
        this.handleNetStateChange(event as im.NetStateChangeEvent);
        break;
      case TIM.EVENT.SDK_RELOAD:
        this.handleSdkReload(event);
        break;
      case TIM.EVENT.MESSAGE_REVOKED:
        this.handleMessageReceived(event as im.MessageReceivedEvent, MessageEventType.messageRevoked);
        break;
      default:
        logger.warn('收到事件，未处理', event);
        break;
    }
  }

  /**
   * 监听事件， 事件名请参考 MessageEventType
   * @param eventName
   * @param callback
   */
  addListener(eventName: MessageEventType, callback: im.TEventCallback): void {
    if (!this.listenerCallback[eventName]) {
      this.listenerCallback[eventName] = [];
    }
    this.listenerCallback[eventName].push(callback);
  }

  /**
   * 移除事件
   * @param eventName
   * @param callback
   */
  removeListener(eventName: MessageEventType, callback: im.TEventCallback): void {
    if (this.listenerCallback[eventName]) {
      const index = this.listenerCallback[eventName].indexOf(callback);
      if (index !== -1) {
        this.listenerCallback[eventName].splice(index, 1);
      }
    }
  }

  /**
   * 获取未读的'@'消息seq集合
   * @param channelId 频道id
   */
  async getUnReadAtMessageSeqList(channelId: string): Promise<number[]> {
    try {
      const response = await Net.request<
      ChannelAtMsgSeqsRsp,
      ChannelAtMsgSeqsReq
      >({
        path: 'knockknock.ImSrv/ChannelAtMsgIds',
        data: {
          channelId,
        },
      });
      return response?.atMsgSeqs?.map(item => parseInt(item, 10)) || [];
    } catch (e) {
      logger.error('getUnReadAtMessage error:', e);
      return [];
    }
  }

  /**
   * 接口需要各方ready后才能调用
   * @param func
   */
  afterReady<T>(func: () => Promise<T>): Promise<T> {
    // readyPromise 表示im已经ready
    return this.readyPromise?.then(() => func.call(this));
  }

  /**
   * 获取会话信息
   * @param conversationID
   * @param isC2C
   */
  async getConversation(conversationID: string, isC2C = false): Promise<im.TConversation> {
    return this.afterReady(() => new Promise((resolve, reject) => {
      const id = this.getConversationId(conversationID, isC2C);
      this.tim.getConversationProfile(id).then(async (result: TIM.GetConversationProfileRsp) => {
        if (result?.data?.conversation) {
          const { conversation } = result.data;
          const lastMessage = await this.transformLastMessage(
            conversationID,
            result.data.conversation.lastMessage as im.TTimMsg,
            isC2C,
          );
          resolve({
            lastMessage,
            unreadCount: conversation.unreadCount,
          });
        } else {
          reject({ msg: 'no conversation info' });
        }
      })
        .catch((e: TIM.TError) => {
          logger.imError(tag, `getConversationProfile fail, conversationID: ${id}, err: `, e);
          reject(e);
        });
    }));
  }

  async getConversationList(
    conversationIDList?: string[],
    isC2C = false,
    callback?: (conversation: im.TConversation) => void,
  ): Promise<Array<im.TConversation>> {
    return this.afterReady(() => {
      const idList = conversationIDList?.map(item => this.getConversationId(item, isC2C));
      return this.tim.getConversationList(idList).then(async (res) => {
        const result: Array<im.TConversation> = [];
        if (conversationIDList?.length) {
          for (const conversationID of conversationIDList) {
            const id = this.getConversationId(conversationID, isC2C);
            const conversationInfo = res.data?.conversationList.find(item => item.conversationID === id);
            const lastMessage = await this.transformLastMessage(
              conversationID,
              conversationInfo?.lastMessage as im.TTimMsg,
              isC2C,
            );
            const data = {
              lastMessage,
              unreadCount: conversationInfo?.unreadCount || 0,
            };
            callback?.(data);
            result.push(data);
          }
        } else if (res.data?.conversationList) {
          const { conversationList } = res.data;
          const prefix = this.getConversationIdPrefix(isC2C);
          for (const item of conversationList) {
            if (item.conversationID.indexOf(prefix) === 0) {
              const lastMessage = await this.transformLastMessage(
                this.transformConversationIdToBusinessId(item.conversationID),
                item.lastMessage as im.TTimMsg,
                isC2C,
              );
              const data = {
                lastMessage,
                unreadCount: item.unreadCount || 0,
              };
              callback?.(data);
              result.push(data);
            }
          }
        }
        return result;
      })
        .catch((err) => {
          logger.imError(tag, `getConversationList fail, idList: ${idList?.join(',') ?? ''}, err: `, err);
          throw err;
        });
    });
  }

  async transformLastMessage(conversationID: string, msg?: im.TTimMsg, isC2C = false): Promise<im.IMsg | null> {
    let lastMessage: im.IMsg | null = null;
    if (this.isLogout) {
      return null;
    }

    if (msg) {
      lastMessage = MessageManager.formatMsg(msg);
    }

    // 新进群可能会拉不到最新的一条消息， 需要调用历史接口拉取
    if (!lastMessage) {
      const historyData = await this.getMessageList(conversationID, {
        isC2C,
        nextReqMessageID: '',
        count: 1,
        needAddUserInfo: false,
      }).catch(() => {
        // 错误已经在getMessageList中打印，无需重复打印
        // logger.error('getConversation, getMessageList', error);
      });

      if (historyData && historyData?.messageList?.length > 0) {
        [lastMessage] = historyData.messageList;
      }
    }

    if (lastMessage) {
      return MessageManager.addUserInfo([lastMessage]).then(([result]) => result);
    }

    return lastMessage;
  }

  /**
   * 拉取历史消息
   * @param conversationID
   * @param params
   */
  async getMessageList(conversationID: string, params?: TGetMessageListParam): Promise<im.GetMessageListRsp> {
    const { count = 30, nextReqMessageID = '', isC2C = false, needAddUserInfo = true } = params || {};
    let traceStartTime = Date.now();
    const traceInfo: TGetMessageTraceInfo = {
      getMessageTime: 0,
      formatMessageTime: 0,
      addUserInfoTime: 0,
    };
    try {
      await this.readyPromise;
      const id = this.getConversationId(conversationID, isC2C);
      let tempMessageList: Array<im.TTimMsg> = [];
      let localCount = count;
      let isCompleted = false;
      let tempNextReqMessageID = nextReqMessageID;
      while (localCount > 0) {
        const tempCount = localCount - 15 > 0 ? 15 : localCount;
        const result: TIM.GetMessageListRsp = await this.tim.getMessageList({
          conversationID: id,
          count: tempCount,
          nextReqMessageID: tempNextReqMessageID,
        });
        const resMessageList: Array<im.TTimMsg> = (result.data.messageList as Array<im.TTimMsg>);
        tempMessageList = resMessageList.concat(tempMessageList);
        tempNextReqMessageID = result.data.nextReqMessageID;
        isCompleted = result.data.isCompleted;
        if (isCompleted) break;
        localCount = localCount - tempCount;
      }
      traceInfo.getMessageTime = Date.now() - traceStartTime;
      traceStartTime = Date.now();
      const messageList: Array<im.IMsg> = (tempMessageList
        .map((message: im.TTimMsg) => MessageManager.formatMsg(message))
        .filter(item => !!item) as Array<im.IMsg>);
      traceInfo.formatMessageTime = Date.now() - traceStartTime;
      traceStartTime = Date.now();
      const messageListWithUserInfo = needAddUserInfo ? await MessageManager.addUserInfo(messageList) : messageList;
      traceInfo.addUserInfoTime = Date.now() - traceStartTime;
      return {
        messageList: messageListWithUserInfo,
        nextReqMessageID: tempNextReqMessageID,
        isCompleted,
        traceInfo,
      };
    } catch (err) {
      logger.imError(tag, 'getMessageList fail, err', err);
      throw err;
    }
  }

  /**
   * 标记消息已读
   * @param conversationID
   * @param isC2C
   */
  setMessageRead(conversationID: string, isC2C = false): Promise<boolean> {
    return this.readyPromise?.then(() => new Promise((resolve, reject) => {
      this.tim.setMessageRead({ conversationID: this.getConversationId(conversationID, isC2C) }).then(() => {
        resolve(true);
        this.triggerListener(MessageEventType.messageRead, conversationID);
      })
        .catch((error: TIM.TError) => {
          logger.imError(tag, `setMessageRead fail, conversationID: ${conversationID}, err: `, error);
          reject(error);
        });
    }));
  }

  /*
  * 获取会话id前缀
  * */
  getConversationIdPrefix(isC2C = false): string {
    return isC2C ? 'C2C' : 'GROUP';
  }

  /*
  * 获取会话id
  * */
  getConversationId(id: string, isC2C = false): string {
    const prefix = this.getConversationIdPrefix(isC2C);
    return `${prefix}${id}`;
  }

  /*
  * 把IM的会话ID，转换成业务的ID
  * */
  transformConversationIdToBusinessId(id: string, isC2C = false): string {
    const prefix = this.getConversationIdPrefix(isC2C);
    return id.replace(prefix, '');
  }

  /**
   * 发送消息
   * @param groupId
   * @param channelId
   * @param baseMessage
   * @param preHandle 发送前的预处理
   * @param isC2C
   */
  async sendMessage<T extends im.IBaseMessage>(
    groupId: string,
    channelId: string,
    baseMessage: T,
    options?: ISendMessageOptions,
  ): Promise<im.IMsg> {
    const preHandle = options?.preHandle ?? null;
    const isC2C = options?.isC2C ?? false;
    const message = await this.constructMessage<T>(groupId, channelId, baseMessage, isC2C);
    return this.sendAndResendMessage(channelId, message, { preHandle, isC2C, isResend: true });
  }

  /**
   * 重发消息
   */
  resendMessage(
    conversationID: string, message: im.IMsg,
    { preHandle = null, isC2C = false }: { preHandle?: im.TPreHandle | null, isC2C?: boolean }
    = { preHandle: null, isC2C: false },
  ): Promise<im.IMsg> {
    return this.sendAndResendMessage(conversationID, message, { preHandle, isC2C, isResend: true });
  }


  /**
   * 构造消息
   * @param groupId
   * @param channelId
   * @param message
   * @param isC2C
   */
  constructMessage<T extends im.IBaseMessage>(groupId: string, channelId: string, message: T, isC2C = false):
  Promise<im.IMsg> {
    const now = Date.now();
    const clientId = `${now}_${Math.floor(Math.random() * 100)}`;
    const { businessPlatform } = getCommonOptions();
    const msg: im.IMsg = {
      type: MsgType.MsgTypeCommon,
      l2Type: message.l2Type,
      l3Types: message.l3Types || [],
      scope: isC2C ? MsgScope.MsgScopeC2C : MsgScope.MsgScopeChannel,
      ts: String(Math.ceil(now / 1000)),
      // usermanager注释
      senderUid: String(UserManager.getInstance().getCurrentUid()),
      content: message?.content || '',
      videoInfo: message?.videoInfo || [],
      /** 图片数据 */
      picInfo: message.picInfo || [],
      /** 文件数据 */
      fileInfo: message.fileInfo || [],
      /** 语音数据 */
      audioInfo: message.audioInfo || [],
      /** 单聊消息结构 */
      c2cMsg: message.c2cMsg,
      /** channel消息结构 */
      channelMsg: {
        channelId,
        groupId,
        msgSeq: '',
        beforeSendChecksum: null,
      },
      /** 回复消息 */
      replyMsg: message.replyMsg,
      /** at消息 */
      atMsg: message.atMsg,
      /** 信令消息 */
      signalingMsg: message.signalingMsg,
      /** 消息摘要 */
      desc: message.desc,
      /** 卡片消息 */
      cardInfo: message.cardInfo || [],
      /** 系统消息 */
      systemMsg: message.systemMsg,
      /** 表情消息 */
      stickerMsg: message.stickerMsg,
      /** 互动类消息 */
      interactionMsg: message.interactionMsg || [],
      /** 混合消息,目前只用于存储顺序 */
      mixedMsg: message.mixedMsg,
      /** 机器人相关数据 */
      botData: message.botData,
      /** 消息版本,编辑消息用 */
      version: '',
      // /** 转发消息 */
      // forwardMsg: null,
      // /** 角色卡消息 */
      // roleCardMsg: null,
      // /** 上报相关参数 */
      // reportParams: null,
      // /** 涂鸦消息 */
      // graffitiMsg: null,
      // /** 结构化消息 */
      // structuredMsg: null,
      // /** 内部参数 */
      // internalParams: null,
      /** 消息话题数据 */
      msgTopicData: message.msgTopicData,
      // /** 话题结构化消息 */
      // topicStructuredMsg: null,
      // /** 自定义消息 */
      // customMsg: null,
      /** 业务平台信息 */
      busiPlatform: businessPlatform,
      // 客户端自定义添加字段
      reportId: clientId,
      // reactions: null,
      clientId,
      sendStatus: im.SendStatusType.init,
      picFileInfo: (message as im.IPicMessage)?.picFileInfo,
      contentLinkMsg: message?.contentLinkMsg,
    };
    return new Promise((resolve, reject) => {
      const simpleUserManager = SimpleUserManager.getInstance();
      const myUid = UserManager.getInstance().getCurrentUid();
      if (!myUid) {
        reject(new Error('constructMessage error: uid is empty'));
        return;
      }

      simpleUserManager.fetchSimpleUserMap([myUid], groupId).then((data: TSimpleUserMap) => {
        if (data[myUid]) {
          msg.senderUserInfo = data[myUid];
          resolve(msg);
        } else {
          resolve(msg);
        }
      })
        .catch((error: RspError) => {
          logger.error('constructMessage error', error);
          resolve(msg);
        });
    });
  }


  /**
   * 撤回消息
   */
  revokeMessage(message: im.IMsg): Promise<RecallGroupMsgRsp> {
    return ImApi.revokeMessage(message.channelMsg.channelId, String(message?.origin?.sequence), message?.origin?.ID || '');
  }

  getBriefMsg(message: im.IMsg): string {
    if (message?.origin?.isRevoked) {
      // TODO: 这里还有管理员撤回等等逻辑
      return '撤回了一条消息';
    }
    if (message.desc) {
      return message.desc.replace(/\r\n|\r|\n/ig, '');
    }
    switch (message.l2Type) {
      case MsgL2Type.MsgL2TypeText:
        return message.content;
      case MsgL2Type.MsgL2TypeAudio:
        return '[语音]';
      case MsgL2Type.MsgL2TypeFile:
        return '[文件]';
      case MsgL2Type.MsgL2TypeMarkdown:
        return message.content;
      case MsgL2Type.MsgL2TypeSystem:
        return message.content;
      case MsgL2Type.MsgL2TypePic:
        return '[图片]';
      case MsgL2Type.MsgL2TypeVideo:
        return '[视频]';
      case MsgL2Type.MsgL2TypeSticker:
        return '[表情]';
      case MsgL2Type.MsgL2TypeForward:
        return '[聊天记录]';
      case MsgL2Type.MsgL2TypeRoleCard:
        return '[档案消息]';
      case MsgL2Type.MsgL2TypeGraffiti:
        return '[涂鸦消息]';
      case MsgL2Type.MsgL2TypeCustomStructured:
        return '[特殊消息]';
      default:
        break;
    }
    return '[无法识别的消息]';
  }

  getDesc(message: im.IMsg): string {
    const { senderUserInfo } = message;
    const nickname = senderUserInfo?.groupNickname || '';
    const briefMsg = this.getBriefMsg(message);
    if (message?.origin?.isRevoked) {
      if (senderUserInfo?.uid.toString() === UserManager.getInstance().getCurrentUid()
        .toString()) {
        return `你${briefMsg}`;
      }
    }
    return `${nickname}: ${briefMsg}`;
  }

  /**
   * 触发事件
   * @param type
   * @param data
   */
  private triggerListener<T>(type: MessageEventType, data: T): void {
    const callbackList = this.listenerCallback[type] ?? [];
    callbackList.forEach((callback) => {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        callback.call(this, data);
      } catch (e) {
        logger.error('triggerListener', e);
      }
    });
  }


  /**
   * 处理SdkReady
   * 收到离线消息和会话列表同步完毕通知，接入侧可以调用 sendMessage 等需要鉴权的接口
   * @param event
   */
  private handleSdkReady(event: im.IBaseEvent): void {
    logger.info('handleSdkReady', event);
    this.isReady = true;
    this.readyResolve?.(1);
    this.triggerListener(MessageEventType.sdkReady, event);
  }

  /**
   * 收到新消息
   * @param event
   * @param type
   */
  private handleMessageReceived(event: im.MessageReceivedEvent, type: MessageEventType): void {
    const messageList: Array<im.IMsg> = [];
    const signalingList: Array<im.IMsg> = [];
    event.data.forEach((item) => {
      const formatItem = MessageManager.formatMsg(item);
      if (formatItem !== null) {
        if (formatItem.scope !== MsgScope.MsgScopeC2C) {
          if (formatItem.l2Type === MsgL2Type.MsgL2TypeSignaling) {
            signalingList.push(formatItem);
          } else {
            messageList.push(formatItem);
          }
        }
      }
      return;
    });

    // 信令消息回调
    if (signalingList.length > 0) {
      this.triggerListener(MessageEventType.signalingReceived, signalingList);
    }

    // 普通消息回调
    if (messageList.length > 0) {
      this.triggerListener(type, messageList);
    }
  }

  /**
   * 被T
   * @param event
   */
  private handleKickedOut(event: im.KickedOutEvent): void {
    this.triggerListener(MessageEventType.kickOut, event);
  }

  /**
   * SDK异常
   * @param event
   */
  private handleSdkError(event: im.IBaseEvent): void {
    this.triggerListener(MessageEventType.sdkError, event);
  }

  /**
   * 网络状态变更
   * @param event
   */
  private handleNetStateChange(event: im.NetStateChangeEvent): void {
    this.triggerListener(MessageEventType.netStateChange, event);
  }

  /**
   * 长时间断网后重新接入网络或者小程序长时间切后台又切回前台，SDK 为了和 IM 服务器进行状态及消息同步，需要重载，此时 SDK 会自动登录
   * @param event
   * @private
   */
  private handleSdkReload(event: im.IBaseEvent): void {
    this.triggerListener(MessageEventType.sdkReload, event);
  }

  /**
   * 发送/重发消息
   * @param conversationID
   * @param messageInfo
   * @param preHandle
   * @param isC2C
   * @param isResend
   * @private
   */
  private async sendAndResendMessage(
    conversationID: string, messageInfo: im.IMsg,
    { preHandle, isC2C, isResend }: { preHandle: im.TPreHandle | null, isC2C: boolean, isResend: boolean }
    = { preHandle: null, isC2C: false, isResend: false },
  ): Promise<im.IMsg> {
    let message = Object.assign({}, messageInfo);
    if (preHandle) {
      message.sendStatus = im.SendStatusType.preHandle;
      // 通知业务侧，有待发送的消息正在处理中
      this.triggerListener(MessageEventType.messageSendStatusChange, message);
      try {
        message = await preHandle(message);
      } catch (e) {
        logger.error(tag, 'preHandle message fail, err: ', e);
        message.sendStatus = im.SendStatusType.error;
        this.triggerListener(MessageEventType.messageSendStatusChange, message);
        return new Promise((resolve, reject) => {
          reject({ errorMsg: 'message preHandle error' });
        });
      }
    }

    message.sendStatus = im.SendStatusType.loading;
    // 通知业务侧，有消息正在发送中
    this.triggerListener(MessageEventType.messageSendStatusChange, message);

    const pbMsg = pbRoot.lookupType('Msg');
    const messageData = pbMsg.create(message);
    const data = this.tim.createCustomMessage({
      to: conversationID,
      conversationType: isC2C ? TIM.TYPES.CONV_C2C : TIM.TYPES.CONV_GROUP,
      payload: {
        data: Base64.fromUint8Array(pbMsg.encode(messageData).finish()),
        description: '',
        extension: '',
      },
    });
    return new Promise((resolve, reject) => {
      const func = isResend ? this.tim.resendMessage : this.tim.sendMessage;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      func(data).then((result: any) => {
        if (result?.code === 0 && result.data) {
          const messageSent = MessageManager.formatMsg(result.data.message);
          if (messageSent) {
            messageSent.clientId = message?.clientId;
            messageSent.senderUserInfo = message.senderUserInfo;
            messageSent.sendStatus = im.SendStatusType.success;
            // 表态初始列表为空
            messageSent.reactions = {
              list: [],
              reactionId: [],
            };
            if (message.replyMsg) {
              report({
                reportParamsList: [
                  {
                    operId: '1813000110602',
                    groupId: messageSent.channelMsg.groupId,
                    channelId: `${messageSent.channelMsg.channelId}`,
                    messageId: `${messageSent.channelMsg.channelId ?? ''}_${messageSent.origin?.sequence ?? ''}`,
                  },
                ],
              });
              report({
                reportParamsList: [
                  {
                    operId: '1913000110602',
                    groupId: messageSent.channelMsg.groupId,
                    channelId: `${messageSent.channelMsg.channelId}`,
                    messageId: `${messageSent.channelMsg.channelId ?? ''}_${messageSent.origin?.sequence ?? ''}`,
                  },
                ],
              });
            } else {
              report({
                reportParamsList: [
                  {
                    operId: '1813000110601',
                    groupId: messageSent.channelMsg.groupId,
                    channelId: `${messageSent.channelMsg.channelId}`,
                    messageId: `${messageSent.channelMsg.channelId ?? ''}_${messageSent.origin?.sequence ?? ''}`,
                  },
                ],
              });
            }
            // 通知业务侧消息发送成功
            this.triggerListener(MessageEventType.messageSendStatusChange, messageSent);
            resolve(messageSent);
          } else {
            logger.error('formatMsg error', result);
            reject({ errorMsg: 'formatMsg error' });
          }
        } else {
          // 通知业务侧消息发送异常
          logger.imError(tag, `${isResend ? 'resendMessage' : 'sendMessage'} fail, res: `, result);
          message.sendStatus = im.SendStatusType.error;
          this.triggerListener(MessageEventType.messageSendStatusChange, message);
          reject({ errorMsg: '无返回信息' });
        }
      })
        .catch((error: TIM.TError) => {
          logger.imError(tag, `${isResend ? 'resendMessage' : 'sendMessage'} fail, err: `, error);
          // 通知业务侧消息发送异常
          message.sendStatus = im.SendStatusType.error;
          this.triggerListener(MessageEventType.messageSendStatusChange, { ...message, code: error.code });
          reject({ errorMsg: error?.message });
        });
    });
  }
}
